
import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  IconButton,
  TableCell,
  TableRow,
  Alert,
  Tooltip,
} from '@mui/material';
import {
  Delete,
  RestoreFromTrash,
  DeleteForever,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import {
  CorporateTable,
  CorporateDialog,
  EmptyState,
  SkeletonLoader,
  CorporateBadge,
} from '../../../components/ui';
import { useToast } from '../../../contexts/ToastContext';
import { apiService } from '../../../services/api';
import { Ensayo } from '../../../types';

export const DeletedEnsayosList: React.FC = () => {
  const { t } = useTranslation();
  const { showError, showSuccess } = useToast();
  const [ensayos, setEnsayos] = useState<Ensayo[]>([]);
  const [loading, setLoading] = useState(true);
  const [openRestoreDialog, setOpenRestoreDialog] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedEnsayo, setSelectedEnsayo] = useState<Ensayo | null>(null);

  useEffect(() => {
    loadEnsayos();
  }, []);

  const loadEnsayos = async () => {
    try {
      setLoading(true);
      const data = await apiService.getDeletedEnsayos();
      setEnsayos(data);
    } catch (error: any) {
      console.error('Error cargando ensayos eliminados:', error);
      showError(error.response?.data?.detail || 'Error al cargar ensayos eliminados');
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = async () => {
    if (!selectedEnsayo) return;
    try {
      await apiService.restoreEnsayo(selectedEnsayo.id);
      showSuccess('Ensayo restaurado correctamente');
      setOpenRestoreDialog(false);
      setSelectedEnsayo(null);
      loadEnsayos();
    } catch (error: any) {
      showError(error.response?.data?.detail || 'Error al restaurar el ensayo');
    }
  };

  const handleHardDelete = async () => {
    if (!selectedEnsayo) return;
    try {
      await apiService.hardDeleteEnsayo(selectedEnsayo.id);
      showSuccess('Ensayo eliminado permanentemente');
      setOpenDeleteDialog(false);
      setSelectedEnsayo(null);
      loadEnsayos();
    } catch (error: any) {
      showError(error.response?.data?.detail || 'Error al eliminar permanentemente el ensayo');
    }
  };

  const openRestoreConfirmation = (ensayo: Ensayo) => {
    setSelectedEnsayo(ensayo);
    setOpenRestoreDialog(true);
  };

  const openDeleteConfirmation = (ensayo: Ensayo) => {
    setSelectedEnsayo(ensayo);
    setOpenDeleteDialog(true);
  };

  const headers = ['Código', 'Nombre', 'Fecha Ensayo', 'Fecha Eliminación', 'Eliminado Por', 'Acciones'];

  if (loading) return <SkeletonLoader variant="table" count={5} columns={6} />;

  if (ensayos.length === 0) {
    return (
      <EmptyState
        icon={<Delete />}
        title="No hay ensayos eliminados"
        description="Los ensayos eliminados aparecerán aquí para su recuperación."
      />
    );
  }

  return (
    <Box>
      <CorporateTable headers={headers} zebraStriping hoverRows>
        {ensayos.map((ensayo) => (
          <TableRow key={ensayo.id}>
            <TableCell>{ensayo.codigo}</TableCell>
            <TableCell>{ensayo.nombre}</TableCell>
            <TableCell>
              {new Date(ensayo.fecha_ensayo).toLocaleDateString('es-ES')}
            </TableCell>
            <TableCell>
              {ensayo.deleted_at 
                ? new Date(ensayo.deleted_at).toLocaleString('es-ES', { 
                    day: '2-digit', 
                    month: '2-digit', 
                    year: 'numeric', 
                    hour: '2-digit', 
                    minute: '2-digit' 
                  })
                : '-'}
            </TableCell>
            <TableCell>
               {/* Asumiendo que deleted_by es un ID, aquí idealmente mostraríamos nombre si tuviéramos join o cache. 
                   Por ahora solo mostramos el ID o "Usuario" si no podemos resolverlo, 
                   o esperamos que el backend pueda poblar un nombre extra.
                   El schema EnsayoOut backend no pobla el nombre del usuario por defecto.
                   Para MVP, mostramos ID o un placeholder. */}
               {ensayo.deleted_by || '-'}
            </TableCell>
            <TableCell>
              <Box display="flex" gap={1}>
                <Tooltip title="Restaurar Ensayo">
                    <IconButton 
                        color="primary" 
                        onClick={() => openRestoreConfirmation(ensayo)}
                    >
                        <RestoreFromTrash />
                    </IconButton>
                </Tooltip>
                
                <Tooltip title="Eliminar Permanentemente (Hard Delete)">
                    <IconButton 
                        color="error" 
                        onClick={() => openDeleteConfirmation(ensayo)}
                    >
                        <DeleteForever />
                    </IconButton>
                </Tooltip>
              </Box>
            </TableCell>
          </TableRow>
        ))}
      </CorporateTable>

      <CorporateDialog
        open={openRestoreDialog}
        onClose={() => setOpenRestoreDialog(false)}
        title="Restaurar Ensayo"
        icon={<RestoreFromTrash />}
        actions={
          <>
            <Button onClick={() => setOpenRestoreDialog(false)}>Cancelar</Button>
            <Button onClick={handleRestore} variant="contained" color="primary">
              Restaurar
            </Button>
          </>
        }
      >
        <Alert severity="info">
          ¿Estás seguro de que deseas restaurar el ensayo <strong>{selectedEnsayo?.codigo}</strong>?
          Volverá a aparecer en el listado principal.
        </Alert>
      </CorporateDialog>

      <CorporateDialog
        open={openDeleteDialog}
        onClose={() => setOpenDeleteDialog(false)}
        title="Eliminación Permanente"
        icon={<DeleteForever />}
        actions={
            <>
              <Button onClick={() => setOpenDeleteDialog(false)}>Cancelar</Button>
              <Button onClick={handleHardDelete} variant="contained" color="error">
                Eliminar Permanentemente
              </Button>
            </>
          }
      >
        <Alert severity="warning">
          ¿Estás seguro de que deseas eliminar <strong>PERMANENTEMENTE</strong> el ensayo <strong>{selectedEnsayo?.codigo}</strong>?
          <br /><br />
          <strong>Esta acción NO SE PUEDE DESHACER.</strong> Se eliminarán todos los datos asociados, incluyendo:
          <ul>
              <li>Archivos Excel (Actuador y Sensores)</li>
              <li>Fotos y Videos subidos</li>
              <li>Cálculos y ajustes</li>
          </ul>
        </Alert>
      </CorporateDialog>
    </Box>
  );
};
